import { z } from "zod";

/**
 * Zod schema for the place_core JSONB column.
 * Validates editorial PlaceCore fields in the admin form.
 *
 * `seasons` (string[]) replaces the legacy `season` (string).
 * `duration` is optional — empty string means "not specified".
 *
 * NOTE: `intents` in place_core is now synced from `place_intents` join table
 * via a DB trigger. The schema still includes it for backwards compat reads,
 * but the canonical SoT is `public.place_intents`.
 */
export const MAX_PLACE_INTENTS = 6;
export const MAX_VIBE_LENGTH = 5000;

export const placeCoreSchema = z.object({
  vibe: z.string().max(MAX_VIBE_LENGTH, `Max ${MAX_VIBE_LENGTH} tecken`).default(""),
  /** Synced from place_intents via trigger. Do NOT write directly. */
  intents: z.array(z.string()).max(MAX_PLACE_INTENTS, `Max ${MAX_PLACE_INTENTS} val`).default([]),
  /** @deprecated Merged into intents. Kept for backward compat reads. */
  character_tags: z.array(z.string()).default([]),
  /** @deprecated Use `seasons` instead. Kept for backward compat reads. */
  season: z.string().default("").optional(),
  /** Multi-select seasons: spring | summer | autumn | winter */
  seasons: z.array(z.string()).default([]),
  /** Optional — empty string / undefined = not specified */
  duration: z.string().default(""),
  did_you_know: z.string().max(200, "Max 200 tecken").default(""),
  area: z.string().default(""),
  /** Canonical field: what a place offers (boende, gardsbutik, etc.) */
  offers: z.array(z.string()).default([]),
  /** Free-text suggestion when "annat" is selected */
  offers_other: z.string().default(""),
  /** Shop product categories — only relevant when "gardsbutik" is in offers */
  shop_categories: z.array(z.string()).default([]),
});

export type PlaceCoreData = z.infer<typeof placeCoreSchema>;

export const EMPTY_PLACE_CORE: PlaceCoreData = {
  vibe: "",
  intents: [],
  character_tags: [],
  season: "",
  seasons: [],
  duration: "",
  did_you_know: "",
  area: "",
  offers: [],
  offers_other: "",
  shop_categories: [],
};

/**
 * Migrate legacy `season` (single string) to `seasons` (array).
 * Called once when loading place data into the editor.
 */
export function migratePlaceCore(raw: Record<string, any>): PlaceCoreData {
  const base = { ...EMPTY_PLACE_CORE, ...raw };

  // Ensure character_tags exists (legacy compat)
  if (!Array.isArray(base.character_tags)) {
    base.character_tags = [];
  }

  // If seasons array is already populated, use it as-is
  if (Array.isArray(base.seasons) && base.seasons.length > 0) {
    return base;
  }

  // Migrate legacy single `season` → `seasons[]`
  if (base.season && typeof base.season === "string") {
    if (base.season === "multi") {
      base.seasons = ["spring", "summer", "autumn", "winter"];
    } else {
      base.seasons = [base.season];
    }
  }

  return base;
}
